import React from 'react'
import {FormattedMessage} from 'react-intl'
import {SponsorImageKey} from '../../types/lessonKeys'
import {SponsorInfo} from '../../types/sponsorAssignments'
import LoadingIndicator from '../LoadingIndicator'
import SponsorCheckbox from '../SponsorCheckbox'

type ModuleRowProps = {
  translationKey: string
  children: React.ReactNode
}

function ModuleRow({
  translationKey,
  children,
  checkboxId
}: ModuleRowProps & {checkboxId?: string}) {
  return (
    <tr className="border-y border-grey-tertiary-30 bg-blue-tertiary-30/15">
      <td className="font-normal py-2n pl-4 pr-3 text-left sm:pl-3">
        {checkboxId ? (
          <label htmlFor={checkboxId} className="cursor-pointer">
            <FormattedMessage id={translationKey} />
          </label>
        ) : (
          <FormattedMessage id={translationKey} />
        )}
      </td>
      <td className="px-3 py-3.5 text-center">
        <div className="flex justify-end pr-[20%]">{children}</div>
      </td>
    </tr>
  )
}

type ModuleAssignmentRowProps = {
  module: {
    type: string
    key: string
    translationKey: string
  }
  assignment: ModuleSponsorAssignment | undefined
  currentSponsor: {id: string; name: string}
  allSponsors: SponsorInfo[]
  onChange: (moduleKey: SponsorImageKey, isAssigned: boolean) => void
}

function ModuleAssignmentRow({
  module,
  assignment,
  currentSponsor,
  allSponsors,
  onChange
}: ModuleAssignmentRowProps) {
  const currentSponsorId = assignment?.sponsorId
  const isAssigned = currentSponsorId === currentSponsor.id

  // If assigned to a different sponsor, show read-only text
  if (currentSponsorId && currentSponsorId !== currentSponsor.id) {
    const assignedSponsor = allSponsors.find(s => s.id === currentSponsorId)
    return (
      <ModuleRow key={module.key} translationKey={module.translationKey}>
        <div className="text-sm py-2 text-center text-grey-tertiary-30">
          <FormattedMessage id="admin.sponsors.assignNational.assigned" /> (
          {assignedSponsor?.name || (
            <FormattedMessage id="admin.sponsors.assignNational.unknown" />
          )}
          )
        </div>
      </ModuleRow>
    )
  }

  // If assigned to current sponsor or not assigned, show checkbox
  const checkboxId = `${module.key}_assignment`
  return (
    <ModuleRow
      key={module.key}
      translationKey={module.translationKey}
      checkboxId={checkboxId}
    >
      <SponsorCheckbox
        id={checkboxId}
        checked={isAssigned}
        onChange={e => {
          onChange(module.key as SponsorImageKey, e.target.checked)
        }}
      />
    </ModuleRow>
  )
}

type Props = {
  assignments: ModuleSponsorAssignment[]
  currentSponsor: {id: string; name: string}
  allSponsors: SponsorInfo[]
  isLoading?: boolean
  onChange: (moduleKey: SponsorImageKey, isAssigned: boolean) => void
}

export type ModuleSponsorAssignment = {
  moduleKey: SponsorImageKey
  sponsorId: string | null
}

// Define the 4 modules that national sponsors can be assigned to
const MODULE_STRUCTURE = [
  {
    type: 'module',
    key: 'MODULE_ONE',
    translationKey: 'admin.sponsors.assignNational.module1'
  },
  {
    type: 'module',
    key: 'MODULE_TWO',
    translationKey: 'admin.sponsors.assignNational.module2'
  },
  {
    type: 'module',
    key: 'MODULE_THREE',
    translationKey: 'admin.sponsors.assignNational.module3'
  },
  {
    type: 'module',
    key: 'MODULE_FOUR',
    translationKey: 'admin.sponsors.assignNational.module4'
  }
] as const

function NationalSponsorAssignmentTable({
  assignments,
  currentSponsor,
  allSponsors,
  isLoading,
  onChange
}: Props) {
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <LoadingIndicator />
      </div>
    )
  }

  // Create a lookup map for assignments by module key
  const assignmentMap = new Map<string, ModuleSponsorAssignment>()
  assignments.forEach(assignment => {
    assignmentMap.set(assignment.moduleKey, assignment)
  })

  return (
    <div className="mt-4 max-h-[32rem] overflow-y-auto rounded-md border border-grey-tertiary-10">
      <table className="w-full">
        <thead className="sticky top-0 z-10 border-b border-grey-tertiary-10 bg-white">
          <tr>
            <th
              scope="col"
              className="bg-white py-3.5 pl-4 pr-3 text-left sm:pl-3"
            >
              <FormattedMessage id="admin.sponsors.assignNational.module" />
            </th>
            <th scope="col" className="bg-white px-3 py-3.5 text-right">
              <FormattedMessage id="admin.sponsors.assignNational.badgeAwards" />
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          {MODULE_STRUCTURE.map(module => (
            <ModuleAssignmentRow
              key={module.key}
              module={module}
              assignment={assignmentMap.get(module.key)}
              currentSponsor={currentSponsor}
              allSponsors={allSponsors}
              onChange={onChange}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default NationalSponsorAssignmentTable
